import 'react-international-phone/style.css';

import { Controller, useForm } from 'react-hook-form';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { City } from 'country-state-city';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import GitHubIcon from '@mui/icons-material/GitHub';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import LockIcon from '@mui/icons-material/Lock';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MuiAutoCompleteCountry from '../components/InputControls/MuiAutoCompleteCountry';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { PhoneInput } from 'react-international-phone';
import { Link as RouterLink } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import Select from '@mui/material/Select';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import api from '../api/axiosInstance';
import { useProfileContext } from '../providers/ProfileProvider';

const CardHeader = ({ title, children }) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <div className='iconBadge'>
          {children}
        </div>
        <Typography variant="xl" fontWeight="medium" component="h3">
          {title}
        </Typography>
      </Box>
      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', mb: 2 }} />
    </>
  );
};

const GENDERS = ['Male', 'Female', 'Other'];

const ACCOUNT_TYPES = ['Employee', 'Intern', 'Candidate', 'Sub Contractor'];

const ROLE_OPTIONS = ['Consultant', 'Intern', 'Consultant Manager', 'HR', 'Sales', 'System Administrator', 'System owner'];

const parseCountry = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value?.label) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed?.label ? parsed : { label: value, code: value };
    } catch {
      return { label: value, code: value };
    }
  }
  return null;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const Account = () => {
  const { profile, loading, refreshProfile } = useProfileContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [jobNotifications, setJobNotifications] = useState(true);
  const [eventNotifications, setEventNotifications] = useState(true);
  const [alumniMember, setAlumniMember] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [newPortfolioLink, setNewPortfolioLink] = useState('');
  const [portfolioError, setPortfolioError] = useState('');

  const defaultValues = useMemo(() => {
    const country = parseCountry(profile?.country);
    return {
      gender: profile?.gender || '',
      dateOfBirth: formatDateForInput(profile?.dateOfBirth) || '',
      residentialAddress: profile?.residentialAddress || profile?.address || '',
      zipCode: profile?.zipCode || '',
      city: profile?.city || '',
      country,
      what3Words: profile?.what3Words || '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      primaryEmail: profile?.email || '',
      secondaryEmail: profile?.secondaryEmail || '',
      phoneNumber: profile?.phoneNumber || '',
      linkedIn: profile?.linkedIn || '',
      github: profile?.github || '',
      figma: profile?.figma || '',
      desiredEmailNativeCoders: profile?.desiredEmailNativeCoders || '',
      workLocationCity: profile?.workLocationCity || '',
      personalIdNumber: profile?.personalIdNumber || '',
      kraPin: profile?.kraPin || '',
      shifNumber: profile?.shifNumber || '',
      bankAccount: profile?.bankAccount || '',
      bankClearingNumber: profile?.bankClearingNumber || '',
      bankNameAndAddress: profile?.bankNameAndAddress || '',
      emergencyContact0: {
        fullName: profile?.emergencyContacts?.[0]?.fullName || '',
        relation: profile?.emergencyContacts?.[0]?.relation || '',
        address: profile?.emergencyContacts?.[0]?.address || '',
        zipCode: profile?.emergencyContacts?.[0]?.zipCode || '',
        city: profile?.emergencyContacts?.[0]?.city || '',
        country: profile?.emergencyContacts?.[0]?.country || '',
        phoneNumber: profile?.emergencyContacts?.[0]?.phoneNumber || '',
        primaryEmail: profile?.emergencyContacts?.[0]?.primaryEmail || '',
      },
      emergencyContact1: {
        fullName: profile?.emergencyContacts?.[1]?.fullName || '',
        relation: profile?.emergencyContacts?.[1]?.relation || '',
        address: profile?.emergencyContacts?.[1]?.address || '',
        zipCode: profile?.emergencyContacts?.[1]?.zipCode || '',
        city: profile?.emergencyContacts?.[1]?.city || '',
        country: profile?.emergencyContacts?.[1]?.country || '',
        phoneNumber: profile?.emergencyContacts?.[1]?.phoneNumber || '',
        primaryEmail: profile?.emergencyContacts?.[1]?.primaryEmail || '',
      },
    };
  }, [profile]);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (profile && Object.keys(defaultValues).length) {
      reset(defaultValues);
      setJobNotifications(profile.jobNotifications !== false);
      setEventNotifications(profile.eventNotifications !== false);
      setAlumniMember(profile.alumniRegistration === true);
    }
  }, [profile, defaultValues, reset]);

  const isEmployee = profile?.accountType === 'Employee';
  const canShowAlumni = profile?.hasEmployeeContract === true;

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    reset(defaultValues);
    setJobNotifications(profile?.jobNotifications !== false);
    setEventNotifications(profile?.eventNotifications !== false);
    setAlumniMember(profile?.alumniRegistration === true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        country: typeof data.country === 'object' && data.country != null
          ? JSON.stringify(data.country)
          : (typeof data.country === 'string' ? data.country : undefined),
        jobNotifications: jobNotifications,
        eventNotifications: eventNotifications,
        alumniRegistration: canShowAlumni ? alumniMember : undefined,
        emergencyContacts: [
          data.emergencyContact0?.fullName ? {
            fullName: data.emergencyContact0.fullName,
            relation: data.emergencyContact0.relation,
            address: data.emergencyContact0.address,
            zipCode: data.emergencyContact0.zipCode,
            city: data.emergencyContact0.city,
            country: data.emergencyContact0.country,
            phoneNumber: data.emergencyContact0.phoneNumber,
            primaryEmail: data.emergencyContact0.primaryEmail,
          } : null,
          data.emergencyContact1?.fullName ? {
            fullName: data.emergencyContact1.fullName,
            relation: data.emergencyContact1.relation,
            address: data.emergencyContact1.address,
            zipCode: data.emergencyContact1.zipCode,
            city: data.emergencyContact1.city,
            country: data.emergencyContact1.country,
            phoneNumber: data.emergencyContact1.phoneNumber,
            primaryEmail: data.emergencyContact1.primaryEmail,
          } : null,
        ].filter(Boolean),
      };
      delete payload.emergencyContact0;
      delete payload.emergencyContact1;
      Object.keys(payload).forEach((k) => payload[k] === '' && (payload[k] = undefined));
      await api.patch('/users/me', payload);
      await refreshProfile();
      setIsEditMode(false);
    } catch (err) {
      console.error('Failed to save account:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await api.delete('/users/me');
      setDeleteConfirmOpen(false);
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to delete account:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const watched = watch();
  const countryCode = watched?.country?.code ?? (typeof watched?.country === 'string' ? (() => { try { const p = JSON.parse(watched.country); return p?.code; } catch { return null; } })() : null);
  const cityOptions = useMemo(() => {
    if (!countryCode) return [];
    return City.getCitiesOfCountry(countryCode) || [];
  }, [countryCode]);

  const prevCountryCodeRef = useRef(null);
  useEffect(() => {
    if (!isEditMode) return;
    const code = countryCode;
    if (prevCountryCodeRef.current !== null && prevCountryCodeRef.current !== code) {
      setValue('city', '');
    }
    prevCountryCodeRef.current = code;
  }, [countryCode, isEditMode, setValue]);

  const canAddMorePortfolio = !watched.github || !watched.figma;

  if (loading) {
    return (
      <main className="main-section">
        <div className="content-section nc-profile">
          <Typography>Loading account...</Typography>
        </div>
      </main>
    );
  }

  return (
    <main className="main-section">
      <div className="content-section nc-profile">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <RouterLink to="/" className="nc-back-link" style={{ fontWeight: 500 }}>
            <ArrowBackIcon />
            <Typography variant="md" component="span" fontWeight="medium">Go Home</Typography>
          </RouterLink>
          {!isEditMode && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              sx={{
                textTransform: 'none',
                borderColor: '#483C3B',
                color: '#483C3B',
                '&:hover': {
                  borderColor: '#483C3B',
                  backgroundColor: 'rgba(72, 60, 59, 0.04)',
                },
              }}
            >
              Edit Account
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="displaySm" component="h1">Account & Security</Typography>
          
          {isEditMode && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit(onSubmit)}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E84610',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#d63d0e',
                  },
                }}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button
                onClick={handleCancel}
                sx={{
                  minWidth: 'auto',
                  width: '40px',
                  height: '56px',
                  padding: 0,
                  color: '#483C3B',
                  '&:hover': {
                    backgroundColor: 'rgba(72, 60, 59, 0.04)',
                  },
                }}
              >
                <CloseIcon />
              </Button>
            </Box>
          )}
        </Box>

        {/* Personal Data Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Personal Data">
              <PersonOutlineIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                
                  <div className="nc-grid-3">
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Gender</Typography>
                      <Typography variant="md" component="p">{profile?.gender || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Date of Birth</Typography>
                      <Typography variant="md" component="p">{formatDateForInput(profile?.dateOfBirth) || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Residential Address</Typography>
                      <Typography variant="md" component="p">{profile?.residentialAddress || profile?.address || '—'}</Typography>
                    </div>
                  </div>
                
                  <div className="nc-grid-3">
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Zip Code</Typography>
                      <Typography variant="md" component="p">{profile?.zipCode || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">City</Typography>
                      <Typography variant="md" component="p">{profile?.city || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Country</Typography>
                      <Typography variant="md" component="p">{parseCountry(profile?.country)?.label || '—'}</Typography>
                    </div>
                  </div>
                
                  <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', pt: 2 }}>
                    <Typography variant="sm" fontWeight="light" component="p">What3Words Location</Typography>
                    <Typography variant="sm" component="p" sx={{ mb: 1 }}>
                      Find your location on{' '}
                      <Link href="https://what3words.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                        what3words.com
                      </Link>
                    </Typography>
                    <Typography variant="md" component="p">{profile?.what3Words || '—'}</Typography>
                  </Box>
                </>
              ) : (
                <>
                 
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Gender</InputLabel>
                          <Select {...field} label="Gender" value={field.value || ''}>
                            {GENDERS.map((gender) => (
                              <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Date of Birth"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            max: new Date().toISOString().split('T')[0],
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="residentialAddress"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Residential Address"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                    <Controller
                      name="zipCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Zip Code"
                        />
                      )}
                    />
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          options={cityOptions.map((c) => c.name)}
                          value={field.value || ''}
                          onInputChange={(_, value) => field.onChange(value)}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="City"
                            />
                          )}
                        />
                      )}
                    />
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Box sx={{
                          '& .MuiAutocomplete-root': { height: '56px' },
                          '& .MuiAutocomplete-root .MuiInputBase-root': { height: '56px', padding: '0 32px 0 16px !important' },
                          '& .MuiAutocomplete-root .MuiInputBase-input': { height: '56px', padding: '0 !important', fontSize: '1rem' },
                          '& .MuiAutocomplete-root .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                          '& .MuiAutocomplete-root .MuiInputLabel-root': { fontSize: '1rem' },
                          '& .MuiAutocomplete-root .MuiInputLabel-shrink': { fontSize: '0.75rem' },
                          '& .MuiAutocomplete-popper': { marginTop: '8px !important' },
                        }}>
                          <MuiAutoCompleteCountry
                            control={control}
                            errors={errors}
                            name="country"
                            label="Country"
                            placeholder="Choose a country"
                          />
                        </Box>
                      )}
                    />
                  </Box>
                  <Typography variant="sm" component="p" sx={{ mb: 2, color: 'text.secondary' }}>
                    We keep your home address on file to help us in case of an emergency and to support work-related needs.
                  </Typography>
                  <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', pt: 2 }}>
                    <Typography variant="md" component="p" fontWeight="medium" sx={{ mb: 1 }}>
                      What3Words Location
                    </Typography>
                    <Typography variant="sm" component="p" sx={{ mb: 2, color: 'text.secondary' }}>
                      Find your residential location on{' '}
                      <Link href="https://what3words.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                        what3words.com
                      </Link>
                    </Typography>
                    <Controller
                      name="what3Words"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Zip Code"
                          placeholder="flatten.pokers.stocks"
                        />
                      )}
                    />
                  </Box>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Identity & Access Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Identity & Access">
              <VpnKeyIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">First Name</Typography>
                      <Typography variant="md" component="p">{profile?.firstName || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Last Name</Typography>
                      <Typography variant="md" component="p">{profile?.lastName || '—'}</Typography>
                    </div>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Primary Email Address</Typography>
                      <Typography variant="md" component="p">{profile?.email || '—'}</Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Secondary Email Address</Typography>
                      <Typography variant="md" component="p">{profile?.secondaryEmail || '—'}</Typography>
                    </div>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Country Code</Typography>
                      <Typography variant="md" component="p">
                        {profile?.phoneNumber ? (profile.phoneNumber.match(/^\+\d{1,4}/)?.[0] || profile.phoneNumber) : '—'}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="sm" fontWeight="light" component="p">Mobile Phone</Typography>
                      <Typography variant="md" component="p">{profile?.phoneNumber || '—'}</Typography>
                    </div>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="sm" fontWeight="light" component="p">LinkedIn</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {profile?.linkedIn ? (
                        <Link
                          href={profile.linkedIn.startsWith('http') ? profile.linkedIn : `https://${profile.linkedIn}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main' }}
                        >
                          {profile.linkedIn.replace(/^https?:\/\//, '')}
                        </Link>
                      ) : (
                        <Typography variant="md" component="span">—</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="md" component="p" sx={{ mb: 0.5 }}>Password</Typography>
                        <Typography variant="sm" fontWeight="light" component="p">Last changed 3 months ago</Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        endIcon={<MoreVertIcon />}
                        onClick={() => setResetPasswordOpen(true)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#483C3B',
                          color: '#483C3B',
                          '&:hover': {
                            borderColor: '#483C3B',
                            backgroundColor: 'rgba(72, 60, 59, 0.04)',
                          },
                        }}
                      >
                        RESET PASSWORD
                      </Button>
                    </Box>
                  </Box>
                  {/* Two-Factor Authentication */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="md" component="p" sx={{ mb: 1 }}>Two-Factor Authentication</Typography>
                        <Typography variant="sm" fontWeight="light" component="p">
                          Add an extra layer of protection to your account by requiring a one-time code from your authenticator app when signing in.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={twoFactorEnabled}
                            onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#483C3B' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#483C3B' },
                            }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="First Name"
                        />
                      )}
                    />
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Last Name"
                        />
                      )}
                    />
                    <Controller
                      name="primaryEmail"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Primary Email Address"
                          type="email"
                        />
                      )}
                    />
                    <Controller
                      name="secondaryEmail"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Secondary Email Address"
                          type="email"
                        />
                      )}
                    />
                    <Box sx={{ gridColumn: { xs: '1', sm: 'span 1' },
                      '& .react-phone-input-2': {
                        height: '56px',
                      },
                      '& .react-phone-input-2 .flag-dropdown': {
                        height: '56px',
                        borderRadius: '4px 0 0 4px',
                      },
                      '& .react-phone-input-2 input': {
                        height: '56px !important',
                        fontSize: '1rem !important',
                        borderRadius: '4px !important',
                        paddingLeft: '48px !important',
                      },
                    }}>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <PhoneInput
                            value={value || ''}
                            onChange={(phone) => {
                              if (!phone || phone.length <= 4) {
                                return;
                              }
                              onChange(phone);
                            }}
                            defaultCountry="ke"
                            preferredCountries={['ke', 'ug', 'tz', 'rw']}
                            style={{
                              width: '100%',
                            }}
                            forceDialCode={true}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name="linkedIn"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="LinkedIn"
                          placeholder="http://linkedin.com/in/janedoe"
                        />
                      )}
                    />
                  </Box>
                </>
              )}
              
              {/* Password Section — faint border (edit mode only; view mode has it above) */}
              {isEditMode && (
                <>
                  <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', mt: 2, pt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="md" component="p" sx={{ mb: 0.5 }}>Password</Typography>
                        <Typography variant="sm" fontWeight="light" component="p">Last changed 5 months ago</Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        endIcon={<MoreVertIcon />}
                        onClick={() => setResetPasswordOpen(true)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#483C3B',
                          color: '#483C3B',
                          '&:hover': {
                            borderColor: '#483C3B',
                            backgroundColor: 'rgba(72, 60, 59, 0.04)',
                          },
                        }}
                      >
                        RESET PASSWORD
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="md" component="p" sx={{ mb: 1 }}>Two-Factor Authentication</Typography>
                        <Typography variant="sm" fontWeight="light" component="p">
                          Add an extra layer of protection to your account by requiring a one-time code from your authenticator app when signing in.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={twoFactorEnabled}
                            onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#483C3B' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#483C3B' },
                            }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee Information (employees only) */}
        {isEmployee && (
          <Card id="profile-info-card" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <CardHeader title="Employee Information">
                <PersonOutlineIcon />
              </CardHeader>
              <div className="nc-card-body">
                {!isEditMode ? (
                  <>
                    <div className="nc-grid-3">
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Desired email at Native Coders</Typography>
                        <Typography variant="md" component="p">{profile?.desiredEmailNativeCoders || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Work location (city)</Typography>
                        <Typography variant="md" component="p">{profile?.workLocationCity || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Personal ID number</Typography>
                        <Typography variant="md" component="p">{profile?.personalIdNumber || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">KRA pin</Typography>
                        <Typography variant="md" component="p">{profile?.kraPin || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">SHIF number</Typography>
                        <Typography variant="md" component="p">{profile?.shifNumber || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Bank account</Typography>
                        <Typography variant="md" component="p">{profile?.bankAccount || '—'}</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Clearing number</Typography>
                        <Typography variant="md" component="p">{profile?.bankClearingNumber || '—'}</Typography>
                      </div>
                    </div>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="sm" fontWeight="light" component="p">Bank name and address</Typography>
                      <Typography variant="md" component="p">{profile?.bankNameAndAddress || '—'}</Typography>
                    </Box>
                    {(profile?.emergencyContacts?.length > 0) && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #EAE7E6' }}>
                        <Typography variant="md" fontWeight="medium" component="p" sx={{ mb: 1 }}>Emergency contacts</Typography>
                        {profile.emergencyContacts.slice(0, 2).map((ec, i) => (
                          <Box key={i} sx={{ mb: 2 }}>
                            <Typography variant="sm" component="p"><strong>{ec.fullName}</strong> — {ec.relation}</Typography>
                            <Typography variant="sm" component="p">{ec.address}, {ec.zipCode} {ec.city}, {ec.country}</Typography>
                            <Typography variant="sm" component="p">{ec.phoneNumber} · {ec.primaryEmail}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                      <Controller name="desiredEmailNativeCoders" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Desired email at Native Coders" placeholder="name.surname@nativecoders.se" />
                      )} />
                      <Controller name="workLocationCity" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Work location (city)" />
                      )} />
                      <Controller name="personalIdNumber" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Personal ID number" />
                      )} />
                      <Controller name="kraPin" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="KRA pin" />
                      )} />
                      <Controller name="shifNumber" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="SHIF number" />
                      )} />
                      <Controller name="bankAccount" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Bank account" />
                      )} />
                      <Controller name="bankClearingNumber" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Clearing number (if applicable)" />
                      )} />
                    </Box>
                    <Controller name="bankNameAndAddress" control={control} render={({ field }) => (
                      <TextField {...field} fullWidth label="Bank name and address" multiline rows={2} sx={{ mb: 2 }} />
                    )} />
                    <Typography variant="md" fontWeight="medium" component="p" sx={{ mb: 1 }}>Emergency contact 1</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                      <Controller name="emergencyContact0.fullName" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Full name" />
                      )} />
                      <Controller name="emergencyContact0.relation" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Relation" />
                      )} />
                      <Controller name="emergencyContact0.address" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Address" />
                      )} />
                      <Controller name="emergencyContact0.zipCode" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Zip code" />
                      )} />
                      <Controller name="emergencyContact0.city" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="City" />
                      )} />
                      <Controller name="emergencyContact0.country" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Country" />
                      )} />
                      <Controller name="emergencyContact0.phoneNumber" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Phone (with country code)" />
                      )} />
                      <Controller name="emergencyContact0.primaryEmail" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Primary email" type="email" />
                      )} />
                    </Box>
                    <Typography variant="md" fontWeight="medium" component="p" sx={{ mb: 1 }}>Emergency contact 2</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <Controller name="emergencyContact1.fullName" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Full name" />
                      )} />
                      <Controller name="emergencyContact1.relation" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Relation" />
                      )} />
                      <Controller name="emergencyContact1.address" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Address" />
                      )} />
                      <Controller name="emergencyContact1.zipCode" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Zip code" />
                      )} />
                      <Controller name="emergencyContact1.city" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="City" />
                      )} />
                      <Controller name="emergencyContact1.country" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Country" />
                      )} />
                      <Controller name="emergencyContact1.phoneNumber" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Phone (with country code)" />
                      )} />
                      <Controller name="emergencyContact1.primaryEmail" control={control} render={({ field }) => (
                        <TextField {...field} fullWidth label="Primary email" type="email" />
                      )} />
                    </Box>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Portfolio Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Portfolio">
              <FolderSharedIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                  {[profile?.github, profile?.figma].filter(Boolean).length ? (
                    <>
                      {profile?.github && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <GitHubIcon sx={{ color: '#483C3B' }} />
                          <Link
                            href={profile.github.startsWith('http') ? profile.github : `https://${profile.github}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main' }}
                          >
                            {profile.github.replace(/^https?:\/\//, '')}
                          </Link>
                        </Box>
                      )}
                      {profile?.figma && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="svg" sx={{ width: 24, height: 24, color: '#483C3B' }} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15.852 8.981h-4.588V0h-1.48v8.981H5.148C4.525 8.981 4 9.49 4 10.18v13.801c0 .69.525 1.019 1.148 1.019h10.704c.623 0 1.148-.39 1.148-1.019V10.18c0-.69-.525-1.199-1.148-1.199zM12.44 20.781c-.72 0-1.3-.58-1.3-1.3s.58-1.3 1.3-1.3 1.3.58 1.3 1.3-.58 1.3-1.3 1.3zm1.28-3.76h-2.56c-.28 0-.5-.22-.5-.5v-2.68c0-.28.22-.5.5-.5h2.56c.28 0 .5.22.5.5v2.68c0 .28-.22.5-.5.5z" />
                          </Box>
                          <Link
                            href={profile.figma.startsWith('http') ? profile.figma : `https://${profile.figma}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main' }}
                          >
                            {profile.figma.replace(/^https?:\/\//, '')}
                          </Link>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="md" component="p" color="text.secondary">No portfolio links yet.</Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="sm" component="p" sx={{ mb: 2, color: 'text.secondary' }}>
                    Add up to 5 links.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Controller
                        name="github"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Github"
                            
                            placeholder="github.com/janedoe"
                          />
                        )}
                      />
                      <Controller
                        name="figma"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Figma"
                            
                            placeholder="figma.com/@janedoe"
                          />
                        )}
                      />
                    </Box>
                    {canAddMorePortfolio && (
                      <Button
                        variant="outlined"
                        startIcon={<Box component="span" sx={{ fontSize: '20px', fontWeight: 'bold' }}>+</Box>}
                        onClick={() => {
                          setNewPortfolioLink('');
                          setPortfolioModalOpen(true);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#483C3B',
                          color: '#483C3B',
                          height: '56px',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#483C3B',
                            backgroundColor: 'rgba(72, 60, 59, 0.04)',
                          },
                        }}
                      >
                        Add Link
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Notifications">
              <NotificationsOutlinedIcon />
            </CardHeader>
            <div className="nc-card-body">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="md" component="p" sx={{ mb: 0.5 }}>Job Opening Notifications</Typography>
                  <Typography variant="sm" fontWeight="light" component="p">
                    Receive notifications about new openings for your roles
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={jobNotifications}
                      onChange={(e) => setJobNotifications(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#483C3B',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#483C3B',
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2 }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="md" component="p" sx={{ mb: 0.5 }}>Event Notifications</Typography>
                  <Typography variant="sm" fontWeight="light" component="p">
                    Receive invites to events and activities from Native Coders
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={eventNotifications}
                      onChange={(e) => setEventNotifications(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#483C3B',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#483C3B',
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            </div>
          </CardContent>
        </Card>

        {/* Community Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Community">
              <PeopleOutlineIcon />
            </CardHeader>
            <div className="nc-card-body">
              {/* First row: intro text */}
              <Typography variant="md" component="p">
                Join the Native Coders Alumni network to stay connected
              </Typography>
              {/* Faint border */}
              <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                {/* Register as Alumni row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="md" component="p" sx={{ mb: 0.5 }}>Register as Alumni</Typography>
                    <Typography variant="sm" fontWeight="light" component="p">
                      Available for former employees to maintain connections and networking opportunities
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={alumniMember}
                        onChange={(e) => setAlumniMember(e.target.checked)}
                        disabled={!canShowAlumni}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#483C3B',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#483C3B',
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Control Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CardHeader title="Privacy & Control">
              <ShieldOutlinedIcon />
            </CardHeader>
            <div className="nc-card-body">
              <Box
                sx={{
                  backgroundColor: '#ffebee',
                  border: '1px solid #ef5350',
                  borderRadius: 1,
                  py: 1.25,
                  px: 2,
                  mb: 2,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <WarningAmberIcon sx={{ color: '#d32f2f', mt: 0.25, fontSize: 20 }} />
                <Typography variant="sm" component="p" sx={{ color: '#d32f2f', lineHeight: 1.4 }}>
                  Deleting your account is permanent and cannot be undone. All your data will be permanently removed.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<DeleteOutlineIcon />}
                disabled={deleteLoading}
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{
                  textTransform: 'none',
                  alignSelf: 'flex-start',
                  width: 'auto',
                  minWidth: 'auto',
                  backgroundColor: '#E84610',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#d63d0e',
                  },
                }}
              >
                {deleteLoading ? 'Deleting…' : 'Delete Account'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Add Portfolio Link Modal */}
        <Dialog
          open={portfolioModalOpen}
          onClose={() => setPortfolioModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5 }}>
            <Typography variant="md" component="span" fontWeight="medium">
              Add Link
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setPortfolioModalOpen(false)}
              sx={{ color: '#483C3B' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1.5 }}>
            <Typography variant="sm" component="p" sx={{ mb: 1 }}>
              Link
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Link"
              value={newPortfolioLink}
              onChange={(e) => {
                setNewPortfolioLink(e.target.value);
                if (portfolioError) setPortfolioError('');
              }}
              error={Boolean(portfolioError)}
              helperText={portfolioError || ' '}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button
              onClick={() => setPortfolioModalOpen(false)}
              sx={{ textTransform: 'none', color: '#483C3B' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: '#E84610',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#d63d0e',
                },
              }}
              onClick={() => {
                const trimmed = newPortfolioLink.trim();
                if (!trimmed) return;

                const normalize = (v) =>
                  v
                    .trim()
                    .replace(/^https?:\/\//i, '')
                    .replace(/\/+$/, '')
                    .toLowerCase();

                const newNorm = normalize(trimmed);
                const githubNorm = watched.github ? normalize(watched.github) : '';
                const figmaNorm = watched.figma ? normalize(watched.figma) : '';

                if (newNorm && (newNorm === githubNorm || newNorm === figmaNorm)) {
                  setPortfolioError('This link is already added.');
                  return;
                }

                if (!watched.github) {
                  setValue('github', trimmed);
                } else if (!watched.figma) {
                  setValue('figma', trimmed);
                }
                setPortfolioModalOpen(false);
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <Typography variant="sm" component="p" sx={{ mb: 2, color: 'text.secondary' }}>
              Your password must be at least 12 characters long and include: At least one uppercase letter (A-Z), at least one lowercase letter (a-z), at least one number (0-9), at least one special character (e.g., ! @ # $ % * &). For best security, avoid using personal information or common words.
            </Typography>
            <TextField fullWidth label="Current Password" type="password" sx={{ mb: 2 }} />
            <TextField fullWidth label="New Password" type="password" sx={{ mb: 2 }} />
            <TextField fullWidth label="Confirm Password" type="password" />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setResetPasswordOpen(false)} sx={{ color: '#483C3B' }}>Cancel</Button>
            <Button variant="contained" sx={{ backgroundColor: '#E84610', color: '#fff' }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete account confirmation */}
        <Dialog open={deleteConfirmOpen} onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}>
          <DialogTitle>Delete account?</DialogTitle>
          <DialogContent>
            <Typography>This action cannot be undone. All your data will be permanently removed.</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteAccount} disabled={deleteLoading}>
              {deleteLoading ? 'Deleting…' : 'Delete account'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </main>
  );
};

export default Account;

